
import type { PropType } from 'vue'
import type { AppMessages } from '../models'
import { defineComponent, ref } from 'vue'
import { EpbInputHelp } from './shared/epb-input-help'

export default defineComponent({
  name: 'UploadFile',
  components: { EpbInputHelp },
  props: {
    disabled: Boolean,
    supportedFormats: {
      type: Array as PropType<Array<string>>,
      required: true,
    },
    messages: {
      type: Object as PropType<AppMessages['uploadFile']>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const baseClass = 'epb-upload-file'
    const fileInput = ref<HTMLInputElement>()
    const fileUploaded = ref<File>()
    const formatError = ref(false)

    function getFileExtension(fileName: string): string {
      return fileName.split('.')[1]
    }

    function isFormatSupported(file: File): boolean {
      return props.supportedFormats.some(
        (extension: string) => extension === getFileExtension(file.name),
      )
    }

    function setDragEffect(event: DragEvent): void {
      if (props.disabled) {
        return
      }
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy'
      }
    }

    function readFile(file: File): void {
      formatError.value = !isFormatSupported(file)

      if (formatError.value) {
        formatError.value = true
        fileUploaded.value = undefined
        emit('uploaded', undefined)
        return
      }
      const reader = new FileReader()
      reader.addEventListener('load', () => {
        fileUploaded.value = file
        emit('uploaded', file)
      })
      reader.readAsText(file)
    }

    function loadFileFromDrop(event: DragEvent): void {
      if (props.disabled) {
        return
      }
      if (event.dataTransfer && fileInput.value) {
        fileInput.value.files = event.dataTransfer.files
        const [file] = fileInput.value.files
        if (file) {
          readFile(file)
        }
      }
    }

    function loadFileFromChange(event: Event): void {
      const fileList = (event.target as HTMLInputElement).files ?? []
      const [file] = fileList
      if (file) {
        readFile(file)
      }
    }

    function reset(): void {
      formatError.value = false
      fileUploaded.value = undefined
      emit('uploaded', undefined)
    }

    return {
      baseClass,
      fileInput,
      fileUploaded,
      formatError,
      loadFileFromChange,
      loadFileFromDrop,
      setDragEffect,
      reset,
    }
  },
})
